/**
 * FO-459 (batch-62) - briefpapier-bibliotheek, keuze bij elke generatie, standaard
 * per template/generatiesoort en facturen via de bibliotheek.
 *
 * Gedeelde unions, labels, Zod-request-schema's en DTO's (API <-> backoffice):
 *  - bibliotheek: varianten per type, bestandsversies, upload-validatie, het
 *    overzicht inclusief expliciet ONTBREKENDE combinaties (BV x variant);
 *  - keuze bij generatie: kiezer-voorstel (cascade), keuze-invoer, keuze-bron;
 *  - standaard per ClientDocumentTemplate of vaste generatiesoort;
 *  - factuur-briefpapier: render-instellingen per sjabloon (vervangen de
 *    module-constanten van de factuur-renderer) en de per-factuur-uitzondering;
 *  - documentweergave: welk briefpapier een document (versie) heeft gekregen.
 *
 * De pure cascade + terugval-resolver staat in @streep/utils (`briefpapier-resolver`).
 * Kopie volgt de STREEP-conventies: "Briefpapier", "Met handtekeningvak",
 * "Zonder handtekeningvak", geen em-dash.
 */
import { z } from 'zod'
import type { HuisstijlSjabloonType } from './huisstijl'

// ─── Varianten ────────────────────────────────────────────────────────────────

export const HUISSTIJL_SJABLOON_VARIANTEN = [
  'MET_HANDTEKENING',
  'ZONDER_HANDTEKENING',
  'STANDAARD',
  'ZONDER_IBAN',
] as const
export type HuisstijlSjabloonVariant = (typeof HUISSTIJL_SJABLOON_VARIANTEN)[number]

/** Varianten van het Word-briefpapier (generatie van documenten). */
export const BRIEFPAPIER_VARIANTEN = ['MET_HANDTEKENING', 'ZONDER_HANDTEKENING'] as const
export type BriefpapierVariant = (typeof BRIEFPAPIER_VARIANTEN)[number]

/** Varianten van het factuur-briefpapier. ZONDER_IBAN = via Finqle (FO-011). */
export const FACTUUR_BRIEFPAPIER_VARIANTEN = ['STANDAARD', 'ZONDER_IBAN'] as const
export type FactuurBriefpapierVariant = (typeof FACTUUR_BRIEFPAPIER_VARIANTEN)[number]

export const HUISSTIJL_VARIANT_LABELS: Record<HuisstijlSjabloonVariant, string> = {
  MET_HANDTEKENING: 'Met handtekeningvak',
  ZONDER_HANDTEKENING: 'Zonder handtekeningvak',
  STANDAARD: 'Factuur standaard',
  ZONDER_IBAN: 'Factuur zonder IBAN (via Finqle)',
}

/** Het variant-deel midden in een zin ("STREEP Service - met handtekeningvak"). */
export const HUISSTIJL_VARIANT_ZINSDEEL: Record<HuisstijlSjabloonVariant, string> = {
  MET_HANDTEKENING: 'met handtekeningvak',
  ZONDER_HANDTEKENING: 'zonder handtekeningvak',
  STANDAARD: 'factuur standaard',
  ZONDER_IBAN: 'factuur zonder IBAN',
}

/** De sjabloontypen die in de briefpapier-bibliotheek beheerd worden. */
export const BIBLIOTHEEK_SJABLOON_TYPEN = ['BRIEFPAPIER_DOCX', 'FACTUUR'] as const
export type BibliotheekSjabloonType = (typeof BIBLIOTHEEK_SJABLOON_TYPEN)[number]

export const BIBLIOTHEEK_TYPE_LABELS: Record<BibliotheekSjabloonType, string> = {
  BRIEFPAPIER_DOCX: 'Briefpapier (documenten)',
  FACTUUR: 'Factuur-briefpapier',
}

/** Welke varianten per sjabloontype geldig zijn. */
export const HUISSTIJL_VARIANTEN_PER_TYPE: Record<
  HuisstijlSjabloonType,
  readonly HuisstijlSjabloonVariant[]
> = {
  BRIEFPAPIER_DOCX: BRIEFPAPIER_VARIANTEN,
  FACTUUR: FACTUUR_BRIEFPAPIER_VARIANTEN,
  EXCEL_HUISSTIJL: ['STANDAARD'],
}

export function isGeldigeVariantVoorType(
  type: HuisstijlSjabloonType,
  variant: HuisstijlSjabloonVariant,
): boolean {
  return HUISSTIJL_VARIANTEN_PER_TYPE[type].includes(variant)
}

export function isBriefpapierVariant(v: string): v is BriefpapierVariant {
  return (BRIEFPAPIER_VARIANTEN as readonly string[]).includes(v)
}

/** Naam van het algemene (BV-loze) briefpapier in de kiezer en de omschrijvingen. */
export const ALGEMEEN_STREEP_NAAM = 'Algemeen STREEP'

/** Omschrijving van het ingebouwde (gebundelde) briefpapier - de laatste terugval. */
export const INGEBOUWD_BRIEFPAPIER_OMSCHRIJVING = 'Algemeen STREEP-briefpapier (ingebouwd)'

/**
 * Leesbare naam van een bibliotheek-combinatie: "STREEP Service - met
 * handtekeningvak (versie 2)". FO-477: bij een briefpapier met een LOS voorblad
 * staat dat achter de variant ("... - met handtekeningvak, voorblad los").
 */
export function briefpapierOmschrijving(args: {
  entityNaam: string | null
  variant: HuisstijlSjabloonVariant
  versieNr?: number | null
  /** FO-477 - het voorblad is een losse pagina; de inhoud begint op het vervolgblad. */
  voorbladLos?: boolean
}): string {
  const basis =
    `${args.entityNaam ?? ALGEMEEN_STREEP_NAAM} - ${HUISSTIJL_VARIANT_ZINSDEEL[args.variant]}` +
    (args.voorbladLos ? `, ${BRIEFPAPIER_VOORBLAD_LOS_ZINSDEEL}` : '')
  return args.versieNr ? `${basis} (versie ${args.versieNr})` : basis
}

// ─── Voorbladmodus (FO-477, batch-64) ────────────────────────────────────────

/**
 * FO-477 - hoe het voorblad van een briefpapier gebruikt wordt:
 *  - `MET_INHOUD`: de inhoud begint op het voorblad (het standaard/algemene
 *    STREEP-briefpapier; pagina 1 heeft daar ruimte voor tekst);
 *  - `LOS`: het voorblad is een eigen pagina ZONDER inhoud (groot logo, tagline,
 *    groene streep) en de inhoud begint op het vervolgblad. Elke inhoudspagina
 *    gebruikt dan het vervolgblad.
 */
export const BRIEFPAPIER_VOORBLAD_MODI = ['MET_INHOUD', 'LOS'] as const
export type BriefpapierVoorbladModus = (typeof BRIEFPAPIER_VOORBLAD_MODI)[number]

export const BRIEFPAPIER_VOORBLAD_MODUS_LABELS: Record<BriefpapierVoorbladModus, string> = {
  MET_INHOUD: 'Inhoud begint op het voorblad',
  LOS: 'Voorblad los (inhoud begint op het vervolgblad)',
}

/** Eenregelige uitleg bij elke keuze (radio in de bibliotheek). */
export const BRIEFPAPIER_VOORBLAD_MODUS_UITLEG: Record<BriefpapierVoorbladModus, string> = {
  MET_INHOUD:
    'Pagina 1 van het briefpapier draagt de eerste pagina tekst. Kies dit voor het standaard STREEP-briefpapier.',
  LOS: 'Pagina 1 blijft een leeg voorblad en de tekst begint op het vervolgblad. Kies dit voor een briefpapier met een eigen voorblad.',
}

/** Het label achter de naam in de briefpapierkeuze en de omschrijvingen. */
export const BRIEFPAPIER_VOORBLAD_LOS_ZINSDEEL = 'voorblad los'

export function isBriefpapierVoorbladModus(v: string): v is BriefpapierVoorbladModus {
  return (BRIEFPAPIER_VOORBLAD_MODI as readonly string[]).includes(v)
}

/**
 * FO-477 §3A.2 / A1 / §8 - de default voorbladmodus van een NIEUW
 * Word-briefpapier, en de ENIGE plek waar die regel staat. Een BV-briefpapier is
 * ontworpen met een eigen voorblad (LOS), het algemene STREEP-briefpapier niet
 * (MET_INHOUD). Zonder vervolgblad in het bestand kan "voorblad los" niet, dus
 * dan MET_INHOUD; `heeftVervolgblad: null` = nog geen bestand om naar te kijken.
 *
 * Elke plek die een `HuisstijlSjabloon` van type BRIEFPAPIER_DOCX aanmaakt hoort
 * deze functie te gebruiken (upload, seed, sjabloonbeheer) - anders krijgt de
 * nieuwe rij de Prisma-default MET_INHOUD en loopt de brieftekst weer over het logo.
 */
export function standaardVoorbladModus(
  entityId: string | null,
  heeftVervolgblad: boolean | null = null,
): BriefpapierVoorbladModus {
  if (entityId === null) return 'MET_INHOUD'
  return heeftVervolgblad === false ? 'MET_INHOUD' : 'LOS'
}

/** De modus als boolean voor de render-laag (overlay, marges, Word-reskin). */
export function voorbladLosVan(modus: BriefpapierVoorbladModus): boolean {
  return modus === 'LOS'
}

/** Welk blad van het briefpapier onder een uitvoerpagina ligt. */
export const BRIEFPAPIER_BLADEN = ['VOORBLAD', 'VERVOLGBLAD'] as const
export type BriefpapierBlad = (typeof BRIEFPAPIER_BLADEN)[number]

export interface BriefpapierPaginaPlanRegel {
  /** Welk blad van het briefpapier de achtergrond levert. */
  blad: BriefpapierBlad
  /** Paginanummer (0-based) IN het briefpapier-bestand dat de achtergrond levert. */
  achtergrondIndex: number
  /** Welke inhoudspagina hierop komt; null = lege pagina (het losse voorblad). */
  inhoudIndex: number | null
}

/**
 * FO-477 §3B - de GEDEELDE beslissing "welke achtergrond hoort bij welke
 * uitvoerpagina". Zowel de pdf-lib-overlay (`overlayOnBriefpapier`) als de
 * Word-reskin (`reskinWordDocx`, embed-modus) leidt zijn gedrag hiervan af, zodat
 * het PDF- en het Word-eindproduct nooit uit elkaar lopen (spec T8).
 *
 * Regels:
 *  - `MET_INHOUD` (huidig gedrag): inhoudspagina 1 op het voorblad, de rest op
 *    het vervolgblad;
 *  - `LOS`: eerst het voorblad als eigen pagina ZONDER inhoud, daarna elke
 *    inhoudspagina op het vervolgblad;
 *  - een briefpapier van één pagina heeft geen vervolgblad: dan is `LOS` niet
 *    uitvoerbaar en valt het plan terug op `MET_INHOUD` (het voorblad is dan ook
 *    het vervolgblad). Dat is bewust geen fout in de render - de bibliotheek
 *    blokkeert die combinatie al bij het activeren (spec §6.2/T4) - zodat een
 *    verkeerd gezette modus nooit een document zonder briefpapier oplevert.
 */
export function briefpapierPaginaPlan(args: {
  inhoudPaginas: number
  briefpapierPaginas: number
  voorbladLos: boolean
}): BriefpapierPaginaPlanRegel[] {
  const inhoud = Math.max(0, Math.trunc(args.inhoudPaginas))
  if (inhoud === 0) return []
  const bladen = Math.max(1, Math.trunc(args.briefpapierPaginas))
  const vervolgIndex = bladen >= 2 ? 1 : 0
  const los = args.voorbladLos && bladen >= 2
  const plan: BriefpapierPaginaPlanRegel[] = []
  if (los) {
    plan.push({ blad: 'VOORBLAD', achtergrondIndex: 0, inhoudIndex: null })
    for (let i = 0; i < inhoud; i++) {
      plan.push({ blad: 'VERVOLGBLAD', achtergrondIndex: vervolgIndex, inhoudIndex: i })
    }
    return plan
  }
  for (let i = 0; i < inhoud; i++) {
    plan.push(
      i === 0
        ? { blad: 'VOORBLAD', achtergrondIndex: 0, inhoudIndex: 0 }
        : { blad: 'VERVOLGBLAD', achtergrondIndex: vervolgIndex, inhoudIndex: i },
    )
  }
  return plan
}

/**
 * Wordt het voorblad een LOSSE (lege) pagina? Zelfde beslissing als het plan
 * hierboven, voor paden die geen paginatelling hebben (Word-reskin: het aantal
 * inhoudspagina's is in de docx-XML niet bekend).
 */
export function losVoorbladNodig(args: { briefpapierPaginas: number; voorbladLos: boolean }): boolean {
  const plan = briefpapierPaginaPlan({ inhoudPaginas: 1, ...args })
  return plan[0]?.inhoudIndex === null
}

// ─── BV-modus (standaard per template/soort) ──────────────────────────────────

export const BRIEFPAPIER_BV_MODI = ['VOLG_CONTEXT', 'VASTE_BV', 'ALGEMEEN'] as const
export type BriefpapierBvModus = (typeof BRIEFPAPIER_BV_MODI)[number]

export const BRIEFPAPIER_BV_MODUS_LABELS: Record<BriefpapierBvModus, string> = {
  VOLG_CONTEXT: 'Volg de context (BV van medewerker of relatie)',
  VASTE_BV: 'Altijd een vaste BV',
  ALGEMEEN: 'Altijd algemeen STREEP',
}

// ─── Documentsoorten (generatiepaden) ─────────────────────────────────────────

/**
 * De generatiepaden die briefpapier gebruiken. De sleutel is de "documentsoort"
 * voor het onthouden per gebruiker en de standaard per soort; template-
 * gebaseerde paden onthouden per template (`briefpapierSoortSleutel`).
 */
export const BRIEFPAPIER_DOCUMENTSOORTEN = [
  'KLANTDOCUMENT',
  'MEDEWERKERDOCUMENT',
  'KLANTOVEREENKOMST',
  'ARBEIDSOVEREENKOMST',
  'AANZEGBRIEF',
  'BRUIKLEEN',
  'OFFBOARDING_DOCUMENT',
  'KLANT_ONBOARDING',
  'ONBOARDING_DOCUMENT',
  'WELKOMSBRIEF',
  'KANDIDAAT_AANBIEDING',
  'GESPREKSVERSLAG',
  'WERKGEVERSVERKLARING',
  'AANMELDPAKKET',
  'HUISSTIJL_CONVERSIE',
] as const
export type BriefpapierDocumentsoort = (typeof BRIEFPAPIER_DOCUMENTSOORTEN)[number]

export interface BriefpapierDocumentsoortDef {
  label: string
  /** Variant zolang beheer niets instelt. */
  standaardVariant: BriefpapierVariant
  /**
   * Gaat dit document bij het genereren een tekenflow in? Stuurt de
   * handtekeningvak-waarschuwing; een pad dat per generatie weet of er getekend
   * wordt (bruikleen, contract, `requiresSignature`) geeft het expliciet mee.
   */
  ondertekening: boolean
  /** Komt de inhoud uit een ClientDocumentTemplate (standaard dan per template). */
  templateGebaseerd: boolean
}

export const BRIEFPAPIER_DOCUMENTSOORT_DEFS: Record<
  BriefpapierDocumentsoort,
  BriefpapierDocumentsoortDef
> = {
  // Generieke klant-/medewerkerdocumenten gaan bij het genereren geen tekenflow
  // in (ondertekenen loopt daarna via het vervolgblok, FO-415, of een pad dat
  // `requiresSignature`/`ondertekening` expliciet meegeeft): geen ruis-waarschuwing.
  KLANTDOCUMENT: {
    label: 'Klantdocument (overeenkomst, opdrachtbevestiging)',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: true,
  },
  MEDEWERKERDOCUMENT: {
    label: 'Medewerkerdocument (overeenkomst)',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: true,
  },
  KLANTOVEREENKOMST: {
    label: 'Klantovereenkomst',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: true,
    templateGebaseerd: true,
  },
  ARBEIDSOVEREENKOMST: {
    label: 'Arbeidsovereenkomst',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: true,
    templateGebaseerd: true,
  },
  AANZEGBRIEF: {
    label: 'Aanzegbrief',
    standaardVariant: 'ZONDER_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: true,
  },
  BRUIKLEEN: {
    label: 'Bruikleendocument',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: true,
    templateGebaseerd: true,
  },
  OFFBOARDING_DOCUMENT: {
    label: 'Getuigschrift of eindebrief',
    standaardVariant: 'ZONDER_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: true,
  },
  KLANT_ONBOARDING: {
    label: 'Onboarding-document relatie',
    standaardVariant: 'ZONDER_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: true,
  },
  ONBOARDING_DOCUMENT: {
    label: 'Onboarding-document medewerker',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: true,
    templateGebaseerd: true,
  },
  WELKOMSBRIEF: {
    label: 'Welkomsbrief',
    standaardVariant: 'ZONDER_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: true,
  },
  KANDIDAAT_AANBIEDING: {
    label: 'Kandidaat-aanbieding',
    standaardVariant: 'ZONDER_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: true,
  },
  GESPREKSVERSLAG: {
    label: 'Gespreksverslag',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: true,
    templateGebaseerd: false,
  },
  WERKGEVERSVERKLARING: {
    label: 'Werkgeversverklaring',
    standaardVariant: 'MET_HANDTEKENING',
    ondertekening: true,
    templateGebaseerd: false,
  },
  AANMELDPAKKET: {
    label: 'Aanmeldpakket (voorblad)',
    standaardVariant: 'ZONDER_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: false,
  },
  HUISSTIJL_CONVERSIE: {
    label: 'Huisstijl-conversie (los Word-bestand)',
    standaardVariant: 'ZONDER_HANDTEKENING',
    ondertekening: false,
    templateGebaseerd: false,
  },
}

/** De documentsoort-sleutel van een factuur (DocumentBriefpapierGebruik.documentsoort). */
export const FACTUUR_DOCUMENTSOORT = 'FACTUUR'

/** Sleutel voor onthouden/vastleggen: per template (`template:<id>`) of per vaste soort. */
export function briefpapierSoortSleutel(
  documentsoort: BriefpapierDocumentsoort,
  templateId?: string | null,
): string {
  return templateId ? `template:${templateId}` : documentsoort
}

export function isBriefpapierDocumentsoort(v: string): v is BriefpapierDocumentsoort {
  return (BRIEFPAPIER_DOCUMENTSOORTEN as readonly string[]).includes(v)
}

// ─── Keuze-bron + toepassing ──────────────────────────────────────────────────

export const BRIEFPAPIER_KEUZE_BRONNEN = [
  'EXPLICIET',
  'TEMPLATE_STANDAARD',
  'SOORT_STANDAARD',
  'GEBRUIKER_VOORKEUR',
  'CONTEXT',
  'ALGEMEEN',
  'FACTUUR_BV_STANDAARD',
  'FACTUUR_UITZONDERING',
  'FINQLE_VAST',
  'VASTGELEGD',
] as const
export type BriefpapierKeuzeBron = (typeof BRIEFPAPIER_KEUZE_BRONNEN)[number]

export const BRIEFPAPIER_KEUZE_BRON_LABELS: Record<BriefpapierKeuzeBron, string> = {
  EXPLICIET: 'Gekozen bij het genereren',
  TEMPLATE_STANDAARD: 'Standaard van het template',
  SOORT_STANDAARD: 'Standaard van deze documentsoort',
  GEBRUIKER_VOORKEUR: 'Je laatste keuze voor deze documentsoort',
  CONTEXT: 'BV van de medewerker of relatie',
  ALGEMEEN: 'Algemeen STREEP (geen BV bekend)',
  FACTUUR_BV_STANDAARD: 'Factuur-briefpapier van de BV',
  FACTUUR_UITZONDERING: 'Uitzondering op deze factuur',
  FINQLE_VAST: 'Via Finqle: altijd zonder IBAN',
  VASTGELEGD: 'Vastgelegd bij definitief maken',
}

export const BRIEFPAPIER_TOEPASSINGEN = [
  'BIBLIOTHEEK',
  'INGEBOUWD',
  'SJABLOON_EIGEN',
  'LEGACY_PAD',
  'GEEN',
] as const
export type BriefpapierToepassingSoort = (typeof BRIEFPAPIER_TOEPASSINGEN)[number]

export const BRIEFPAPIER_TOEPASSING_LABELS: Record<BriefpapierToepassingSoort, string> = {
  BIBLIOTHEEK: 'Briefpapier uit de bibliotheek',
  INGEBOUWD: 'Ingebouwd STREEP-briefpapier',
  SJABLOON_EIGEN: 'Eigen briefpapier van het sjabloon',
  LEGACY_PAD: 'Factuur-briefpapier van de BV (nog niet gemigreerd)',
  GEEN: 'Geen briefpapier',
}

/** Waarschuwingscodes van de briefpapierkeuze (ook in DocumentGeneratieWaarschuwingCode). */
export type BriefpapierWaarschuwingCode =
  /** Het gekozen briefpapier (BV x variant) ontbreekt; er is een terugval gebruikt. */
  | 'BRIEFPAPIER_ONTBREEKT'
  /** Te ondertekenen document op briefpapier zonder handtekeningvak. */
  | 'BRIEFPAPIER_ZONDER_HANDTEKENINGVAK'
  /** Sjabloon met eigen briefpapier + expliciete keuze: risico op een dubbele kop/voet. */
  | 'BRIEFPAPIER_DUBBEL_RISICO'
  /**
   * FO-485 (batch-65) - het render-vangnet heeft voorbeeldtekst uit de kop/voet
   * van het briefpapier verwijderd. Nooit stil: de melding komt op het document.
   */
  | 'BRIEFPAPIER_VOORBEELDTEKST_VERWIJDERD'
  /**
   * FO-485 §5.2 - sjabloon-eigen briefpapier met een voorblad maar zonder
   * vervolgblad: het voorblad kan niet los, de inhoud begint op het voorblad.
   */
  | 'BRIEFPAPIER_GEEN_VERVOLGBLAD'
  /**
   * FO-485 - sjabloon-eigen briefpapier is herkend als een bibliotheekversie die
   * op "inhoud begint op het voorblad" staat, terwijl pagina 1 op een voorblad
   * lijkt. De keuze van de bibliotheek wint, maar niet stil.
   */
  | 'BRIEFPAPIER_VOORBLAD_MET_INHOUD'
  /**
   * FO-485 - het afgeleide LOSSE voorblad van sjabloon-eigen briefpapier kon bij
   * de render niet worden toegepast (het document liet zich niet splitsen): de
   * inhoud begint op het voorblad.
   */
  | 'BRIEFPAPIER_VOORBLAD_NIET_TOEGEPAST'

export interface BriefpapierWaarschuwing {
  code: BriefpapierWaarschuwingCode
  melding: string
}

// ─── Keuze-invoer (generatie-requests) ────────────────────────────────────────

/**
 * De briefpapierkeuze die een generatie-request meestuurt (kiezer). Ontbreekt
 * hij, dan kiest de cascade (automatische paden, oude clients).
 * `entityId: null` = algemeen STREEP-briefpapier.
 */
export const BriefpapierKeuzeInvoerSchema = z
  .object({
    entityId: z.string().trim().min(1).max(64).nullable(),
    variant: z.enum(BRIEFPAPIER_VARIANTEN),
  })
  .strict()
export type BriefpapierKeuzeInvoer = z.infer<typeof BriefpapierKeuzeInvoerSchema>

/**
 * De briefpapierkeuze als query-parameters, voor GET-downloads (aanmeldpakket):
 * `briefpapierBv` = BV-id of "algemeen", plus de variant. Beide of geen.
 */
export const BriefpapierKeuzeQuerySchema = z.object({
  briefpapierBv: z.string().trim().min(1).max(64).optional(),
  briefpapierVariant: z.enum(BRIEFPAPIER_VARIANTEN).optional(),
})
export type BriefpapierKeuzeQuery = z.infer<typeof BriefpapierKeuzeQuerySchema>

/** De keuze uit de query-parameters; null zolang niet beide gezet zijn (cascade). */
export function briefpapierKeuzeUitQuery(q: BriefpapierKeuzeQuery): BriefpapierKeuzeInvoer | null {
  if (!q.briefpapierBv || !q.briefpapierVariant) return null
  return {
    entityId: q.briefpapierBv === 'algemeen' ? null : q.briefpapierBv,
    variant: q.briefpapierVariant,
  }
}

/** De omgekeerde vorm: keuze -> query-parameters (voor een relatieve download-link). */
export function briefpapierKeuzeNaarQuery(k: BriefpapierKeuzeInvoer): Record<string, string> {
  return { briefpapierBv: k.entityId ?? 'algemeen', briefpapierVariant: k.variant }
}

// ─── Kiezer: voorstel + beschikbaarheid ───────────────────────────────────────

/** Een beschikbare bibliotheek-combinatie (actief, met bestand). */
export interface BriefpapierCombinatieDTO {
  entityId: string | null
  variant: BriefpapierVariant
  sjabloonId: string
  versieNr: number | null
  /** FO-477 - het voorblad is een losse pagina (label "voorblad los" in de kiezer). */
  voorbladLos: boolean
}

export interface BriefpapierBvOptieDTO {
  id: string
  naam: string
}

export interface BriefpapierVoorstelDTO {
  /** Soort-sleutel (onthouden/vastleggen). */
  soortSleutel: string
  documentsoort: BriefpapierDocumentsoort
  documentsoortLabel: string
  templateId: string | null
  /** De voorgevulde keuze (cascade) en waar hij vandaan komt. */
  voorstel: BriefpapierKeuzeInvoer
  voorstelBron: BriefpapierKeuzeBron
  contextEntityId: string | null
  /** Alle actieve BV's (voor "overrule naar een andere BV"). */
  bvOpties: BriefpapierBvOptieDTO[]
  /** Wat de bibliotheek werkelijk heeft (voor de live resultaat-/terugvalregel). */
  beschikbaar: BriefpapierCombinatieDTO[]
  /** Wordt dit document ondertekend (waarschuwing bij "zonder handtekeningvak")? */
  ondertekening: boolean
  /** Draagt het sjabloon zijn eigen briefpapier (FO-410-vlag)? */
  eigenBriefpapierSjabloon: boolean
  /** De standaard die voor dit template/deze soort is ingesteld (null = ingebouwd). */
  standaard: BriefpapierStandaardDTO | null
}

/** Wat een generatie feitelijk toepast (kiezer-resultaat en generatie-resultaat). */
export interface BriefpapierToepassingDTO {
  gevraagd: BriefpapierKeuzeInvoer
  keuzeBron: BriefpapierKeuzeBron
  toepassing: BriefpapierToepassingSoort
  sjabloonId: string | null
  versieId: string | null
  versieNr: number | null
  entityId: string | null
  variant: HuisstijlSjabloonVariant | null
  omschrijving: string
  /**
   * FO-477 - het toegepaste briefpapier heeft een LOS voorblad: de eerste pagina
   * blijft leeg en de inhoud begint op het vervolgblad. FO-485: een bibliotheek-
   * briefpapier volgt zijn sjabloon; sjabloon-eigen briefpapier krijgt de modus
   * van de herkende bibliotheekversie of uit de analyse (`voorbladBron`). Het
   * ingebouwde briefpapier en "geen briefpapier" zijn altijd false.
   */
  voorbladLos: boolean
  /** True als het toegepaste briefpapier niet het gevraagde is. */
  terugval: boolean
  waarschuwingen: BriefpapierWaarschuwing[]
  /**
   * FO-485 (batch-65) - de kale naam, zonder versie en voorblad ("STREEP
   * Projecten - zonder handtekeningvak"); `briefpapierResultaatRegel` zet daar
   * versie, voorbladmodus en route achter. Optioneel: toepassingen die vóór
   * FO-485 zijn vastgelegd (conversie-opties, JSON) missen hem.
   */
  briefpapierNaam?: string
  /**
   * FO-485 - waar de voorbladmodus vandaan komt (zie `BriefpapierVoorbladBron`).
   * Optioneel om dezelfde reden; lees hem via `voorbladBronVan`.
   */
  voorbladBron?: BriefpapierVoorbladBron
  /**
   * FO-485 - de voorbeeldtekst die het render-vangnet uit de kop/voet van het
   * briefpapier heeft verwijderd (leeg of afwezig = niets). De melding staat
   * óók in `waarschuwingen` (BRIEFPAPIER_VOORBEELDTEKST_VERWIJDERD), zodat hij
   * bij het document wordt vastgelegd.
   */
  saneringen?: BriefpapierSaneringRegel[]
}

// ─── Voorbladbron + resultaatregel (FO-485, batch-65) ─────────────────────────

/**
 * FO-485 §4 - waar de voorbladmodus van een generatie vandaan komt:
 *  - `BIBLIOTHEEK`   : de modus van een bibliotheek-sjabloon. Ook bij sjabloon-
 *                      eigen briefpapier dat als bibliotheekversie is herkend
 *                      (hash van de kop-afbeeldingen);
 *  - `AFGELEID`      : sjabloon-eigen briefpapier, niet herkend, maar de analyse
 *                      ziet een voorblad (paginavullend beeld + vervolgblad): LOS;
 *  - `GEEN_VOORBLAD` : geen los voorblad (ingebouwd, geen briefpapier, of
 *                      sjabloon-eigen briefpapier zonder voorblad): MET_INHOUD.
 */
export const BRIEFPAPIER_VOORBLAD_BRONNEN = ['BIBLIOTHEEK', 'AFGELEID', 'GEEN_VOORBLAD'] as const
export type BriefpapierVoorbladBron = (typeof BRIEFPAPIER_VOORBLAD_BRONNEN)[number]

/** De route van een generatie in de resultaatregel ("route: bibliotheek"). */
export const BRIEFPAPIER_ROUTE_LABELS: Record<BriefpapierToepassingSoort, string> = {
  BIBLIOTHEEK: 'bibliotheek',
  INGEBOUWD: 'ingebouwd',
  SJABLOON_EIGEN: 'sjabloon-eigen',
  LEGACY_PAD: 'oud factuurpad',
  GEEN: 'geen briefpapier',
}

/** Het deel tussen haakjes achter "route: sjabloon-eigen". */
export const BRIEFPAPIER_VOORBLAD_BRON_LABELS: Record<BriefpapierVoorbladBron, string> = {
  BIBLIOTHEEK: 'herkend in de bibliotheek',
  AFGELEID: 'afgeleid',
  GEEN_VOORBLAD: 'geen los voorblad',
}

/**
 * De voorbladbron van een toepassing, ook voor een toepassing die vóór FO-485
 * is vastgelegd: een bibliotheek-briefpapier volgt zijn sjabloon, al het andere
 * heeft geen los voorblad (dat kon toen alleen de bibliotheek).
 */
export function voorbladBronVan(
  t: Pick<BriefpapierToepassingDTO, 'toepassing' | 'voorbladBron'>,
): BriefpapierVoorbladBron {
  if (t.voorbladBron) return t.voorbladBron
  return t.toepassing === 'BIBLIOTHEEK' ? 'BIBLIOTHEEK' : 'GEEN_VOORBLAD'
}

/**
 * FO-485 §8 - de regel op het resultaatpaneel van ELK gegenereerd document:
 *   "Briefpapier: STREEP Projecten - zonder handtekeningvak v3 - voorblad los - route: bibliotheek"
 *   "Briefpapier: Eigen briefpapier van het sjabloon - voorblad los - route: sjabloon-eigen (afgeleid)"
 * Zo is bij een volgende afwijking direct te zien welke route, versie en
 * voorbladmodus gebruikt zijn. De saneer-melding staat apart
 * (`briefpapierSaneerMelding`). STREEP-copy, geen em-dash.
 */
export function briefpapierResultaatRegel(t: BriefpapierResultaatInvoer): string {
  return `Briefpapier: ${briefpapierResultaatOmschrijving(t)}`
}

type BriefpapierResultaatInvoer = Pick<
  BriefpapierToepassingDTO,
  'toepassing' | 'omschrijving' | 'versieNr' | 'voorbladLos' | 'briefpapierNaam' | 'voorbladBron'
>

/**
 * Dezelfde regel zonder het voorvoegsel "Briefpapier: ", voor een resultaat-
 * paneel dat dat label zelf al toont.
 */
export function briefpapierResultaatOmschrijving(t: BriefpapierResultaatInvoer): string {
  // Een vastlegging van vóór FO-485 heeft alleen de omschrijving, en daar staat
  // ", voorblad los" al in: die komt hieronder als eigen deel, dus niet dubbel.
  const naam = t.briefpapierNaam ?? t.omschrijving.replace(`, ${BRIEFPAPIER_VOORBLAD_LOS_ZINSDEEL}`, '')
  const versie = t.briefpapierNaam && t.versieNr ? ` v${t.versieNr}` : ''
  const delen = [`${naam}${versie}`]
  if (t.toepassing !== 'GEEN') {
    delen.push(t.voorbladLos ? BRIEFPAPIER_VOORBLAD_LOS_ZINSDEEL : 'inhoud begint op het voorblad')
  }
  const route = BRIEFPAPIER_ROUTE_LABELS[t.toepassing] ?? t.toepassing
  delen.push(
    t.toepassing === 'SJABLOON_EIGEN'
      ? `route: ${route} (${BRIEFPAPIER_VOORBLAD_BRON_LABELS[voorbladBronVan(t)]})`
      : `route: ${route}`,
  )
  return delen.join(' - ')
}

/** De saneer-meldingen van een toepassing (FO-485 §2.2), voor het resultaatpaneel. */
export function briefpapierSaneerMeldingen(t: Pick<BriefpapierToepassingDTO, 'waarschuwingen'>): string[] {
  return t.waarschuwingen
    .filter((w) => w.code === 'BRIEFPAPIER_VOORBEELDTEKST_VERWIJDERD')
    .map((w) => w.melding)
}

// ─── Voorbeeldtekst saneren (FO-485, batch-65) ────────────────────────────────

/**
 * Waarom een alinea als voorbeeldtekst is aangemerkt:
 *  - `PATROON`      : de alinea bevat een voorbeeldtekst-patroon ("lorem",
 *                     "TITEL VAN HET DOCUMENT", ...). Voorstel: verwijderen.
 *  - `PAGINA_TEKST` : lopende tekst op de PAGINA van een bibliotheek-briefpapier
 *                     (niet in een tekstvak). Een briefpapier heeft daar niets te
 *                     zeggen: het is de voorbeeldbrief van de ontwerper, vaak in
 *                     nep-Latijn zonder "lorem". Voorstel: verwijderen.
 *  - `LANGE_ALINEA` : meer dan 40 woorden lopende tekst in een kop (of een
 *                     tekstvak op de pagina), zonder patroon. Kan een legitieme
 *                     regel zijn (algemene voorwaarden): voorstel UIT, en de
 *                     render laat hem staan.
 *  - `BEDRIJFSGEGEVENS` : een regel op de pagina die op bedrijfsgegevens lijkt
 *                     (adres, KvK/BTW/IBAN, telefoon, e-mail, website). Die horen
 *                     te blijven (spec §2.2): voorstel UIT, de render laat hem
 *                     staan, maar hij staat wel in het voorstel (nooit stil).
 */
export const BRIEFPAPIER_SANEER_REDENEN = ['PATROON', 'LANGE_ALINEA', 'PAGINA_TEKST', 'BEDRIJFSGEGEVENS'] as const
export type BriefpapierSaneerReden = (typeof BRIEFPAPIER_SANEER_REDENEN)[number]

export const BRIEFPAPIER_SANEER_REDEN_LABELS: Record<BriefpapierSaneerReden, string> = {
  PATROON: 'Voorbeeldtekst',
  LANGE_ALINEA: 'Lange alinea zonder voorbeeldtekst',
  PAGINA_TEKST: 'Tekst op de pagina van het briefpapier',
  BEDRIJFSGEGEVENS: 'Bedrijfsgegevens op de pagina',
}

/** Waar in het bestand een fragment staat. */
export const BRIEFPAPIER_SANEER_DELEN = ['KOP', 'VOET', 'BODY'] as const
export type BriefpapierSaneerDeel = (typeof BRIEFPAPIER_SANEER_DELEN)[number]

export const BRIEFPAPIER_SANEER_DEEL_LABELS: Record<BriefpapierSaneerDeel, string> = {
  KOP: 'Kop',
  VOET: 'Voet',
  BODY: 'Pagina',
}

/** Eén kop-/voet-alinea die als voorbeeldtekst is gevonden (upload-voorstel). */
export interface BriefpapierSaneerFragmentDTO {
  /** Stabiel per bestand: part + hash van de tekst ("word/header1.xml#1a2b3c4d5e"). */
  id: string
  /** Part-naam in het bestand, bv. `word/header1.xml`. */
  part: string
  /**
   * KOP/VOET: een kop- of voettekst. BODY: de pagina zelf van een bibliotheek-
   * briefpapier (`word/document.xml`) - die wordt als achtergrond meegerenderd,
   * dus tekst daarin komt net zo goed op elk document.
   */
  soort: BriefpapierSaneerDeel
  /** De alineatekst (lange tekst ingekort). */
  tekst: string
  woorden: number
  /** De gevonden voorbeeldtekst-patronen (leeg bij LANGE_ALINEA). */
  patronen: string[]
  reden: BriefpapierSaneerReden
  /** De alinea staat in een tekstvak (w:txbxContent). */
  inTekstvak: boolean
  /** Het voorgestelde vinkje: PATROON en PAGINA_TEKST aan, LANGE_ALINEA en BEDRIJFSGEGEVENS uit (spec §5.1). */
  standaardAan: boolean
  /** Hoe vaak deze tekst in dit part staat (de oude-Word-kopie van een tekstvak telt niet). */
  aantal: number
}

/**
 * Stand van de sanering van één sjabloonversie:
 *  - `VOORGESTELD` : bij de upload gevonden, nog niet bevestigd. Het bestand is
 *                    het origineel; het render-vangnet haalt de voorbeeldtekst
 *                    weg bij elke generatie (met waarschuwing op het document);
 *  - `GESANEERD`   : bevestigd; het bestand van de versie is de gesaneerde kopie,
 *                    het origineel staat onder `bronBestandKey`;
 *  - `ORIGINEEL`   : bewust niets verwijderd (alle vinkjes uit) of hersteld.
 */
export const BRIEFPAPIER_SANERING_STATUSSEN = ['VOORGESTELD', 'GESANEERD', 'ORIGINEEL'] as const
export type BriefpapierSaneringStatus = (typeof BRIEFPAPIER_SANERING_STATUSSEN)[number]

export const BRIEFPAPIER_SANERING_STATUS_LABELS: Record<BriefpapierSaneringStatus, string> = {
  VOORGESTELD: 'Voorbeeldtekst gevonden, nog niet beoordeeld',
  GESANEERD: 'Voorbeeldtekst verwijderd',
  ORIGINEEL: 'Origineel bestand (niets verwijderd)',
}

/** Een fragment met de keuze die voor deze versie geldt. */
export interface BriefpapierSaneerKeuzeDTO extends BriefpapierSaneerFragmentDTO {
  /** Verwijderd uit het bestand van de versie (false = blijft staan). */
  verwijderd: boolean
}

/**
 * Opgeslagen in `HuisstijlSjabloonVersie.gesaneerdeFragmenten`: ALLE fragmenten
 * die in het ORIGINELE bestand zijn gevonden, met per fragment de keuze. Een
 * fragment dat bewust blijft staan (`verwijderd: false`) respecteert ook het
 * render-vangnet: beheer heeft er dan over beslist.
 */
export interface BriefpapierSaneringDTO {
  status: BriefpapierSaneringStatus
  fragmenten: BriefpapierSaneerKeuzeDTO[]
  /** User.id (of "systeem:<script>") van de laatste keuze; null bij VOORGESTELD. */
  door: string | null
  /** ISO-tijdstip van de laatste keuze; null bij VOORGESTELD. */
  op: string | null
}

/** Een door de render verwijderd fragment (op het document vastgelegd). */
export interface BriefpapierSaneringRegel {
  part: string
  soort: BriefpapierSaneerDeel
  tekst: string
  /**
   * Waar het stond: het briefpapier (bibliotheek of sjabloon-eigen) of de eigen
   * kop/voet van het document. Optioneel: regels van vóór deze uitbreiding.
   */
  herkomst?: BriefpapierSaneerHerkomst
}

/** Het aantal verwijderde fragmenten van een versie (badge "voorbeeldtekst verwijderd (n)"). */
export function aantalVerwijderdeFragmenten(s: BriefpapierSaneringDTO | null | undefined): number {
  if (!s || s.status !== 'GESANEERD') return 0
  return s.fragmenten.filter((f) => f.verwijderd).length
}

/** De badge-tekst in de bibliotheek, of null als er niets verwijderd is. */
export function briefpapierSaneerBadge(s: BriefpapierSaneringDTO | null | undefined): string | null {
  const n = aantalVerwijderdeFragmenten(s)
  return n > 0 ? `voorbeeldtekst verwijderd (${n})` : null
}

/** Korte weergave van een fragment in een melding: eerste woorden, met "...". */
export function saneerFragmentKort(tekst: string, max = 60): string {
  const t = tekst.replace(/\s+/g, ' ').trim()
  return t.length > max ? `${t.slice(0, max - 3).trimEnd()}...` : t
}

/**
 * Waar het render-vangnet voorbeeldtekst vond - dat bepaalt waar het opgelost
 * hoort te worden:
 *  - `BIBLIOTHEEK`    : in het briefpapier uit de bibliotheek (beoordelen in
 *                       Huisstijl > Briefpapier);
 *  - `SJABLOON_EIGEN` : in het briefpapier dat het sjabloon zelf draagt;
 *  - `DOCUMENT`       : in de eigen kop of voet van het sjabloon of bronbestand
 *                       dat op bibliotheek-briefpapier wordt gezet (die kop/voet
 *                       rendert anders gewoon mee).
 */
export type BriefpapierSaneerHerkomst = 'BIBLIOTHEEK' | 'SJABLOON_EIGEN' | 'DOCUMENT'

/**
 * De melding op het document als het render-vangnet voorbeeldtekst verwijderde
 * (spec §2.2): "Voorbeeldtekst uit het briefpapier verwijderd: 3 fragmenten
 * ("SUBTITEL", "TITEL VAN HET DOCUMENT", "Lorem ipsum dolor ..."). ..." plus waar
 * het opgelost hoort te worden.
 */
export function briefpapierSaneerMelding(
  regels: readonly BriefpapierSaneringRegel[],
  route: BriefpapierSaneerHerkomst,
): string | null {
  if (regels.length === 0) return null
  const voorbeelden = regels
    .slice(0, 3)
    .map((r) => `"${saneerFragmentKort(r.tekst, 40)}"`)
    .join(', ')
  const rest = regels.length > 3 ? ` en ${regels.length - 3} meer` : ''
  const advies =
    route === 'BIBLIOTHEEK'
      ? 'Beoordeel de voorbeeldtekst van deze versie in Huisstijl > Briefpapier.'
      : route === 'SJABLOON_EIGEN'
        ? 'Haal de tekst uit de kop of voet van het sjabloon.'
        : 'Haal de tekst uit de kop of voet van het sjabloon of bronbestand.'
  const waar = route === 'DOCUMENT' ? 'de kop of voet van het document' : 'het briefpapier'
  return (
    `Voorbeeldtekst uit ${waar} verwijderd: ${regels.length} ${
      regels.length === 1 ? 'fragment' : 'fragmenten'
    } (${voorbeelden}${rest}). ${advies}`
  )
}

/**
 * Body van `POST /huisstijl/briefpapier/versies/:id/saneren`: welke fragmenten
 * uit het bestand gaan (de vinkjes). Een lege lijst = het origineel herstellen.
 */
export const BriefpapierSanerenInvoerSchema = z
  .object({
    verwijderFragmentIds: z.array(z.string().trim().min(1).max(300)).max(500),
  })
  .strict()
export type BriefpapierSanerenInvoer = z.infer<typeof BriefpapierSanerenInvoerSchema>

// ─── Bibliotheek: versies, validatie, overzicht ───────────────────────────────

export const HUISSTIJL_SJABLOON_VERSIE_STATUSSEN = [
  'CONCEPT',
  'ACTIEF',
  'VERVANGEN',
  'VERWORPEN',
] as const
export type HuisstijlSjabloonVersieStatus = (typeof HUISSTIJL_SJABLOON_VERSIE_STATUSSEN)[number]

export const HUISSTIJL_SJABLOON_VERSIE_STATUS_LABELS: Record<HuisstijlSjabloonVersieStatus, string> = {
  CONCEPT: 'Wacht op controle',
  ACTIEF: 'Actief',
  VERVANGEN: 'Vervangen',
  VERWORPEN: 'Verworpen',
}

/** Upload-validatierapport van een briefpapierbestand. */
export interface BriefpapierValidatie {
  bestandssoort: 'DOCX' | 'PDF'
  /** DOCX: detectDocxBriefpapier vond kop-/voet-afbeeldingen. */
  briefpapierGedetecteerd: boolean | null
  afbeeldingRelaties: number | null
  achtergrondLagen: number | null
  /** Paginaformaat in millimeters (afgerond). */
  paginaFormaat: { breedteMm: number; hoogteMm: number } | null
  isA4: boolean
  /** DOCX-marges in millimeters (afgerond). */
  marges: { bovenMm: number; rechtsMm: number; onderMm: number; linksMm: number } | null
  margesPlausibel: boolean | null
  /** Aantal pagina's (1 = voorblad = vervolgblad, 2 = voorblad + vervolgblad). DOCX: de ondergrens uit de sectie-/pagina-einden. */
  paginas: number | null
  /**
   * FO-477 - heeft dit briefpapier een vervolgblad (pagina 2)? `false` betekent
   * dat "voorblad los" niet kan (spec §6.2/T4); `null` = niet vastgesteld
   * (oudere versies van vóór FO-477, of een onleesbaar bestand).
   */
  heeftVervolgblad: boolean | null
  /**
   * FO-477 §3C.9 - hoeveel van het inhoudsgebied van pagina 1 (respectievelijk
   * het vervolgblad) door een afbeelding bedekt wordt, 0..1. Voedt de
   * "waarschijnlijk een voorblad"-waarschuwing; `null` bij een PDF of een
   * bestand zonder kop-afbeeldingen.
   */
  voorbladDekking: number | null
  vervolgbladDekking: number | null
  /**
   * FO-477 §3C.8 - gevonden voorbeeldtekst in de kop-/voet-parts (incl.
   * tekstvakken), als korte fragmenten. Leeg = niets gevonden; `null` = niet
   * onderzocht (PDF of onleesbaar bestand).
   */
  voorbeeldtekstFragmenten: string[] | null
  /** Blokkerend: het bestand kan niet als briefpapier gebruikt worden. */
  fouten: string[]
  /** Niet-blokkerend: controleer het voorbeeld extra goed. */
  waarschuwingen: string[]
}

/**
 * Status van een combinatie (BV x variant) in het overzicht. ONTBREEKT geldt
 * ook voor het algemene briefpapier zonder eigen upload: dan noemt
 * `terugvalOmschrijving` het ingebouwde STREEP-briefpapier dat de generatie gebruikt.
 * CONCEPT = er is (nog) geen actieve versie, wel een upload die op controle wacht.
 */
export const BRIEFPAPIER_BIBLIOTHEEK_STATUSSEN = [
  'ACTIEF',
  'CONCEPT',
  'GEDEACTIVEERD',
  'ONTBREEKT',
] as const
export type BriefpapierBibliotheekStatus = (typeof BRIEFPAPIER_BIBLIOTHEEK_STATUSSEN)[number]

export const BRIEFPAPIER_BIBLIOTHEEK_STATUS_LABELS: Record<BriefpapierBibliotheekStatus, string> = {
  ACTIEF: 'Actief',
  CONCEPT: 'Wacht op controle',
  GEDEACTIVEERD: 'Gedeactiveerd',
  ONTBREEKT: 'Ontbreekt',
}

export interface BriefpapierVersieDTO {
  id: string
  versieNr: number
  status: HuisstijlSjabloonVersieStatus
  fileName: string
  fileSize: number | null
  validatie: BriefpapierValidatie | null
  geuploadDoorNaam: string | null
  geuploadOp: string
  /** Wanneer het voorbeeld (productie-pijplijn) het laatst geopend is; activeren vereist dit. */
  voorbeeldBekekenOp: string | null
  voorbeeldGecontroleerdOp: string | null
  voorbeeldGecontroleerdDoorNaam: string | null
  geactiveerdOp: string | null
  /**
   * FO-485 - de voorbeeldtekst-sanering van deze versie: het voorstel (bij de
   * upload) of de bevestigde keuze. Null = geen voorbeeldtekst gevonden, of een
   * versie die het saneerscript nog niet beoordeeld heeft.
   */
  sanering: BriefpapierSaneringDTO | null
  /** FO-485 - het originele bestand is apart bewaard (de versie draagt een gesaneerde kopie). */
  heeftOrigineel: boolean
}

export interface BriefpapierBibliotheekRijDTO {
  /** `${type}:${entityId ?? 'algemeen'}:${variant}` - stabiele rij-sleutel. */
  sleutel: string
  type: BibliotheekSjabloonType
  entityId: string | null
  entityNaam: string
  variant: HuisstijlSjabloonVariant
  status: BriefpapierBibliotheekStatus
  sjabloonId: string | null
  actieveVersie: BriefpapierVersieDTO | null
  /** Nieuwste concept dat op controle wacht (voorbeeld + activeren). */
  conceptVersie: BriefpapierVersieDTO | null
  /** Oudere versies (vervangen/verworpen), nieuwste eerst, max. 5. */
  historie: BriefpapierVersieDTO[]
  /** Alleen FACTUUR: de render-instellingen van dit briefpapier. */
  renderInstellingen: FactuurRenderInstellingen | null
  /**
   * FO-477 - alleen BRIEFPAPIER_DOCX: hoe het voorblad gebruikt wordt. Zonder
   * sjabloon (status ONTBREEKT) de default die een nieuwe upload zou krijgen.
   */
  voorbladModus: BriefpapierVoorbladModus | null
  /**
   * FO-477 §3C.9 - "pagina 1 lijkt een voorblad, kies Voorblad los", beoordeeld
   * op de HUIDIGE modus (niet op de modus van het moment van uploaden). Null als
   * er niets te melden is of de dekking niet is vastgesteld.
   */
  voorbladWaarschuwing: string | null
  /** Bij ONTBREEKT/GEDEACTIVEERD: wat een generatie dan gebruikt. */
  terugvalOmschrijving: string | null
}

export interface BriefpapierBibliotheekDTO {
  rijen: BriefpapierBibliotheekRijDTO[]
  telling: { totaal: number; actief: number; concept: number; ontbrekend: number }
  /** Sjablonen buiten het raster (oude dubbele niet-standaard-rijen), ter info. */
  overig: Array<{
    id: string
    naam: string
    type: HuisstijlSjabloonType
    entityNaam: string | null
    variant: HuisstijlSjabloonVariant
    actief: boolean
  }>
}

/** Multipart-velden bij het uploaden van een nieuwe briefpapierversie. */
export const BriefpapierUploadVeldenSchema = z
  .object({
    type: z.enum(BIBLIOTHEEK_SJABLOON_TYPEN),
    entityId: z.string().trim().min(1).max(64).nullable(),
    variant: z.enum(HUISSTIJL_SJABLOON_VARIANTEN),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (!isGeldigeVariantVoorType(v.type, v.variant)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['variant'],
        message: 'Deze variant hoort niet bij dit soort briefpapier.',
      })
    }
  })
export type BriefpapierUploadVelden = z.infer<typeof BriefpapierUploadVeldenSchema>

const VOORBEELD_NIET_GECONTROLEERD =
  'Bekijk eerst het voorbeeld en vink "Voorbeeld gecontroleerd" aan.'

/** Activeren van een conceptversie: het voorbeeld moet gecontroleerd zijn. */
export const BriefpapierActiverenSchema = z
  .object({
    voorbeeldGecontroleerd: z.literal(true, { message: VOORBEELD_NIET_GECONTROLEERD }),
  })
  .strict()
export type BriefpapierActiveren = z.infer<typeof BriefpapierActiverenSchema>

/** FO-477 - de voorbladmodus van een Word-briefpapier-sjabloon zetten. */
export const BriefpapierVoorbladModusInvoerSchema = z
  .object({ voorbladModus: z.enum(BRIEFPAPIER_VOORBLAD_MODI) })
  .strict()
export type BriefpapierVoorbladModusInvoer = z.infer<typeof BriefpapierVoorbladModusInvoerSchema>

/**
 * FO-477 §6.2 - "voorblad los" kan alleen op een briefpapier met een
 * vervolgblad. Zelfde tekst in de bibliotheek (bij het zetten van de modus) en
 * bij het activeren van een versie, zodat de uitleg overal gelijk is.
 */
export const BRIEFPAPIER_GEEN_VERVOLGBLAD_MELDING =
  'Dit briefpapier heeft maar één pagina en dus geen vervolgblad. Kies "Inhoud begint op het voorblad", of upload een bestand met een voorblad en een vervolgblad.'

// ─── Standaard per template / generatiesoort ──────────────────────────────────

export const BriefpapierStandaardInvoerSchema = z
  .object({
    variant: z.enum(BRIEFPAPIER_VARIANTEN),
    bvModus: z.enum(BRIEFPAPIER_BV_MODI),
    entityId: z.string().trim().min(1).max(64).nullable(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.bvModus === 'VASTE_BV' && !v.entityId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entityId'],
        message: 'Kies de vaste BV.',
      })
    }
  })
export type BriefpapierStandaardInvoer = z.infer<typeof BriefpapierStandaardInvoerSchema>

export interface BriefpapierStandaardDTO {
  templateId: string | null
  documentsoort: BriefpapierDocumentsoort | null
  variant: BriefpapierVariant
  bvModus: BriefpapierBvModus
  entityId: string | null
  entityNaam: string | null
  /** "Volg de context - met handtekeningvak" / "Altijd STREEP Personeel - zonder handtekeningvak". */
  omschrijving: string
  /** False = geen beheer-instelling; de ingebouwde soort-default geldt. */
  ingesteld: boolean
  gewijzigdOp: string | null
}

export function briefpapierStandaardOmschrijving(s: {
  bvModus: BriefpapierBvModus
  entityNaam: string | null
  variant: BriefpapierVariant
}): string {
  const bv =
    s.bvModus === 'VASTE_BV'
      ? `Altijd ${s.entityNaam ?? 'vaste BV'}`
      : s.bvModus === 'ALGEMEEN'
        ? `Altijd ${ALGEMEEN_STREEP_NAAM}`
        : 'Volg de context'
  return `${bv} - ${HUISSTIJL_VARIANT_ZINSDEEL[s.variant]}`
}

export interface BriefpapierStandaardenOverzichtDTO {
  /** Per vaste generatiesoort (ook zonder instelling, met ingesteld=false). */
  soorten: Array<BriefpapierStandaardDTO & { documentsoort: BriefpapierDocumentsoort; label: string }>
}

// ─── Factuur: render-instellingen ─────────────────────────────────────────────

/**
 * Layout van het factuur-briefpapier:
 *  - INGEBAKKEN_KOP: per-BV factuurpapier met de BV-kop en -voet (KVK/BTW/IBAN)
 *    in het papier zelf; referentieblok rechtsboven vanaf `contentTop`.
 *  - AFZENDERBLOK: algemeen briefpapier zonder BV-gegevens; de renderer tekent
 *    het BV-afzenderblok + de kop "Factuur" als tekst vanaf `eersteBladTop`,
 *    verankert de totalen en de cessietekst onderaan (FO-011 vervolg).
 */
export const FACTUUR_LAYOUTS = ['INGEBAKKEN_KOP', 'AFZENDERBLOK'] as const
export type FactuurLayout = (typeof FACTUUR_LAYOUTS)[number]

export const FACTUUR_LAYOUT_LABELS: Record<FactuurLayout, string> = {
  INGEBAKKEN_KOP: 'Factuurpapier met BV-kop en -voet in het papier',
  AFZENDERBLOK: 'Algemeen briefpapier: BV-afzender als tekst',
}

/**
 * Render-coordinaten (PDF-punten, oorsprong linksonder, A4 = 595 x 842) per
 * factuur-briefpapier. Vervangen de vroegere module-constanten van
 * `invoice-pdf.ts`, zodat een briefpapier met andere marges de tekst niet laat
 * botsen.
 */
export interface FactuurRenderInstellingen {
  layout: FactuurLayout
  /** Linker-/rechtergrens van de content (was CONTENT_LEFT/CONTENT_RIGHT). */
  contentLeft: number
  contentRight: number
  /** Bovenkant van de content op vervolgpagina's en (INGEBAKKEN_KOP) op pagina 1 (was CONTENT_TOP). */
  contentTop: number
  /** Onder deze y breekt de content naar een nieuwe pagina (was CONTENT_BOTTOM). */
  contentBottom: number
  /** AFZENDERBLOK: bovenkant van klant- + afzenderblok op pagina 1 (was BRIEFPAPIER_INVOICE_TOP). */
  eersteBladTop: number
  /** Totalen-blok naar deze y verankeren (alleen naar beneden; was BRIEFPAPIER_TOTALS_TOP). Null = direct onder de regels. */
  totalenAnkerY: number | null
  /** y van de laatste regel van de cessietekst onderaan (was LETTERHEAD_CONTENT_BOTTOM + 20). Null = niet verankeren. */
  cessieAnkerY: number | null
  /** Content-zone van bijlage-pagina's op dit papier (was LETTERHEAD_CONTENT_TOP/_BOTTOM). */
  bijlageContentTop: number
  bijlageContentBottom: number
  /** y van de regel "Pagina x / y". */
  pagineringY: number
}

/** Presets = exact de vroegere module-constanten (migratie pixel-identiek). */
export const FACTUUR_RENDER_PRESETS: Record<FactuurLayout, FactuurRenderInstellingen> = {
  INGEBAKKEN_KOP: {
    layout: 'INGEBAKKEN_KOP',
    contentLeft: 50,
    contentRight: 545,
    contentTop: 630,
    contentBottom: 80,
    eersteBladTop: 630,
    totalenAnkerY: null,
    cessieAnkerY: null,
    bijlageContentTop: 700,
    bijlageContentBottom: 130,
    pagineringY: 90,
  },
  AFZENDERBLOK: {
    layout: 'AFZENDERBLOK',
    contentLeft: 50,
    contentRight: 545,
    contentTop: 630,
    contentBottom: 80,
    eersteBladTop: 765,
    totalenAnkerY: 300,
    cessieAnkerY: 150,
    bijlageContentTop: 700,
    bijlageContentBottom: 130,
    pagineringY: 90,
  },
}

const PT_MAX_HOOGTE = 842
const PT_MAX_BREEDTE = 596

const puntY = z.number().finite().min(0).max(PT_MAX_HOOGTE)
const puntX = z.number().finite().min(0).max(PT_MAX_BREEDTE)

export const FactuurRenderInstellingenSchema = z
  .object({
    layout: z.enum(FACTUUR_LAYOUTS),
    contentLeft: puntX,
    contentRight: puntX,
    contentTop: puntY,
    contentBottom: puntY,
    eersteBladTop: puntY,
    totalenAnkerY: puntY.nullable(),
    cessieAnkerY: puntY.nullable(),
    bijlageContentTop: puntY,
    bijlageContentBottom: puntY,
    pagineringY: puntY,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.contentRight - v.contentLeft < 200) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contentRight'],
        message: 'De content moet minstens 200 punten breed zijn.',
      })
    }
    if (v.contentTop - v.contentBottom < 150) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contentTop'],
        message: 'Tussen boven- en ondergrens moet minstens 150 punten ruimte zijn.',
      })
    }
    if (v.eersteBladTop < v.contentBottom + 150) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eersteBladTop'],
        message: 'De bovenkant van pagina 1 ligt te laag.',
      })
    }
    if (v.bijlageContentTop - v.bijlageContentBottom < 150) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['bijlageContentTop'],
        message: 'De bijlage-zone moet minstens 150 punten hoog zijn.',
      })
    }
  })

/**
 * Lees de render-instellingen van een factuur-briefpapier (JSON-kolom). Ongeldig
 * of leeg -> de preset van de meegegeven layout. Nooit een half-geldig object.
 */
export function leesFactuurRenderInstellingen(
  json: unknown,
  fallbackLayout: FactuurLayout,
): FactuurRenderInstellingen {
  const parsed = FactuurRenderInstellingenSchema.safeParse(json)
  return parsed.success ? parsed.data : FACTUUR_RENDER_PRESETS[fallbackLayout]
}

/** Render-instellingen van een factuur-briefpapier wijzigen; null = terug naar de preset. */
export const FactuurRenderInstellingenInvoerSchema = z
  .object({ renderInstellingen: FactuurRenderInstellingenSchema.nullable() })
  .strict()
export type FactuurRenderInstellingenInvoer = z.infer<typeof FactuurRenderInstellingenInvoerSchema>

// ─── Factuur: keuze per factuur ───────────────────────────────────────────────

/** Per-factuur-uitzondering (FACTUREN.canEdit). `sjabloonId: null` = terug naar de BV-standaard. */
export const FactuurBriefpapierKeuzeInvoerSchema = z
  .object({
    sjabloonId: z.string().trim().min(1).max(64).nullable(),
    reden: z.string().trim().max(500).nullable().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.sjabloonId && (!v.reden || v.reden.trim().length < 3)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: 'Geef een reden voor deze uitzondering (minimaal 3 tekens).',
      })
    }
  })
export type FactuurBriefpapierKeuzeInvoer = z.infer<typeof FactuurBriefpapierKeuzeInvoerSchema>

export interface FactuurBriefpapierOptieDTO {
  sjabloonId: string
  entityId: string | null
  entityNaam: string
  variant: FactuurBriefpapierVariant
  versieNr: number | null
  omschrijving: string
}

export interface FactuurBriefpapierDTO {
  invoiceId: string
  /** Loopt deze factuur via Finqle (dan altijd de IBAN-loze variant, FO-011)? */
  viaFinqle: boolean
  /** True = niet aan te passen (via Finqle, of al vastgelegd bij definitief maken). */
  vast: boolean
  vastReden: string | null
  huidig: {
    omschrijving: string
    keuzeBron: BriefpapierKeuzeBron
    toepassing: BriefpapierToepassingSoort
    sjabloonId: string | null
    variant: FactuurBriefpapierVariant
    terugval: boolean
    waarschuwingen: string[]
  }
  uitzondering: {
    sjabloonId: string
    omschrijving: string
    reden: string | null
    gekozenDoorNaam: string | null
    gekozenOp: string
  } | null
  /** Kiesbare factuur-briefpapieren (STANDAARD) voor een uitzondering. */
  opties: FactuurBriefpapierOptieDTO[]
  magWijzigen: boolean
  /** Vastgelegd gebruik (definitief maken, verzenden, Finqle), nieuwste eerst. */
  historie: DocumentBriefpapierGebruikDTO[]
}

// ─── Documentweergave ─────────────────────────────────────────────────────────

export interface DocumentBriefpapierGebruikDTO {
  id: string
  versionNr: number | null
  generatiepad: string
  documentsoort: string
  omschrijving: string
  keuzeBron: BriefpapierKeuzeBron
  keuzeBronLabel: string
  toepassing: BriefpapierToepassingSoort
  toepassingLabel: string
  terugval: boolean
  waarschuwingen: string[]
  gekozenDoorNaam: string | null
  createdAt: string
}

// ─── Opnieuw genereren met ander briefpapier (FO-378) ─────────────────────────

/**
 * Opnieuw genereren met een ander briefpapier = een NIEUWE VERSIE van hetzelfde
 * document; de oude versie houdt zijn briefpapier (FO-036). Kan voor documenten
 * uit een sjabloon (ClientGeneratedDocument); andere gegenereerde documenten
 * krijgen hun briefpapier in hun eigen flow.
 */
export interface DocumentBriefpapierHergeneratieDTO {
  mogelijk: boolean
  /** Waarom het niet kan (ondertekend, sjabloon gewijzigd, ...); null als het kan. */
  reden: string | null
  documentsoort: BriefpapierDocumentsoort
  templateId: string
  /** BV van de context (medewerker of sjabloon) voor het kiezer-voorstel. */
  entityId: string | null
  /** Wordt het document ondertekend (waarschuwing bij "zonder handtekeningvak")? */
  ondertekening: boolean
}

export const BriefpapierHergeneratieInvoerSchema = z
  .object({
    briefpapier: BriefpapierKeuzeInvoerSchema,
    reden: z.string().trim().max(500).nullable().optional(),
  })
  .strict()
export type BriefpapierHergeneratieInvoer = z.infer<typeof BriefpapierHergeneratieInvoerSchema>

export interface BriefpapierHergeneratieResultaatDTO {
  documentId: string
  versionNr: number
  briefpapier: BriefpapierToepassingDTO
  waarschuwingen: BriefpapierWaarschuwing[]
}

// ─── Offerte (FO-459 review H3: de offerte op het PDF-briefpapier van de BV) ──

/** De documentsoort-sleutel van een offerte (DocumentBriefpapierGebruik.documentsoort). */
export const OFFERTE_DOCUMENTSOORT = 'OFFERTE'

/**
 * De briefpapierkeuze voor een offerte-PDF (voorbeeld en klaarzetten): een
 * PDF-briefpapier uit de bibliotheek (type FACTUUR, variant STANDAARD - hetzelfde
 * briefpapier als de facturen van de BV). `sjabloonId: null` = de standaard
 * (het briefpapier van de BV van de offerte, anders algemeen STREEP).
 */
export const OfferteBriefpapierKeuzeInvoerSchema = z
  .object({ sjabloonId: z.string().trim().min(1).max(64).nullable() })
  .strict()
export type OfferteBriefpapierKeuzeInvoer = z.infer<typeof OfferteBriefpapierKeuzeInvoerSchema>

/** Body van POST /quotes/:id/klaarzetten: notitie + optionele briefpapierkeuze. */
export const OfferteKlaarzettenInvoerSchema = z.object({
  note: z.string().max(500).optional(),
  briefpapier: OfferteBriefpapierKeuzeInvoerSchema.nullable().optional(),
})
export type OfferteKlaarzettenInvoer = z.infer<typeof OfferteKlaarzettenInvoerSchema>

/** Een kiesbaar PDF-briefpapier voor een offerte. */
export interface OfferteBriefpapierOptieDTO {
  sjabloonId: string
  entityId: string | null
  entityNaam: string
  versieNr: number | null
  omschrijving: string
}

/** Het briefpapier van één offerte (kaart op de offerte + kiezer bij klaarzetten). */
export interface OfferteBriefpapierDTO {
  quoteId: string
  /** True na klaarzetten: de PDF en zijn briefpapier liggen vast (FO-036). */
  vast: boolean
  /** Wat de offerte-PDF krijgt; null = geen briefpapier (klaarzetten wordt geweigerd). */
  huidig: {
    omschrijving: string
    keuzeBron: BriefpapierKeuzeBron
    keuzeBronLabel: string
    toepassing: BriefpapierToepassingSoort
    sjabloonId: string | null
    terugval: boolean
    waarschuwingen: string[]
  } | null
  /** Waarom er geen briefpapier is (bij huidig = null). */
  ontbreektMelding: string | null
  /** Het PDF-briefpapier van de BV van de offerte in de bibliotheek (null = geen). */
  standaardSjabloonId: string | null
  /** Kiesbare PDF-briefpapieren (BV's + algemeen). */
  opties: OfferteBriefpapierOptieDTO[]
  /** Vastgelegd bij klaarzetten: welk briefpapier, door wie, wanneer. */
  vastgelegd: { omschrijving: string; gekozenDoorNaam: string | null; vastgelegdOp: string } | null
}

/** Herkomst-labels van het offerte-briefpapier (kaart en kiezer op de offerte). */
export const OFFERTE_BRIEFPAPIER_BRON_LABELS = {
  EXPLICIET: 'Gekozen voor deze offerte',
  BV: 'PDF-briefpapier van de BV van de offerte',
  OUD_PAD: 'Offertepapier van de BV',
  ALGEMEEN: 'Algemeen STREEP: de BV heeft geen offertepapier of PDF-briefpapier',
  VASTGELEGD: 'Vastgelegd bij klaarzetten',
} as const

/**
 * Query van GET /quotes/:id/pdf: het voorbeeld van een concept met een ander
 * PDF-briefpapier (kiezer; via de same-origin-proxy). Na klaarzetten genegeerd:
 * de bewaarde PDF ligt vast (FO-036).
 */
export const OfferteBriefpapierQuerySchema = z.object({
  briefpapierSjabloonId: z.string().trim().min(1).max(64).optional(),
})
export type OfferteBriefpapierQuery = z.infer<typeof OfferteBriefpapierQuerySchema>
